import React, { useState, useEffect, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import tradingApi from '../../../services/tradingApi';
import useLanguage from '../../../hooks/useLanguage';

const PositionsPanel = () => {
  const [activeTab, setActiveTab] = useState('positions');
  const [positions, setPositions] = useState([]);
  const [pendingOrders, setPendingOrders] = useState([]);
  const [tradeHistory, setTradeHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const { t, isRTL } = useLanguage();

  useEffect(() => {
    loadTradingData();
    connectToWebSocket();

    return () => {
      tradingApi?.disconnectWebSocket();
    };
  }, []);

  const handlePositionUpdate = (data) => {
    setPositions(prev => {
      const index = prev?.findIndex(p => p?.id === data?.id);
      if (index >= 0) {
        // Update existing position
        const updated = [...prev];
        updated[index] = { ...updated?.[index], ...data };
        return updated;
      } else if (data?.status === 'open') {
        // Add new position
        return [...prev, data];
      }
      return prev;
    });
  };

  const handleOrderUpdate = (data) => {
    setPendingOrders(prev => {
      const index = prev?.findIndex(o => o?.id === data?.id);
      if (index >= 0) {
        if (data?.status === 'cancelled' || data?.status === 'filled') {
          // Remove cancelled or filled orders
          return prev?.filter(o => o?.id !== data?.id);
        } else {
          // Update existing order
          const updated = [...prev];
          updated[index] = { ...updated?.[index], ...data };
          return updated;
        }
      } else if (data?.status === 'pending') {
        // Add new pending order
        return [...prev, data];
      }
      return prev;
    });
  };

  const loadTradingData = async () => {
    try {
      setLoading(true);
      
      // Load positions, orders, and history from Zonex API
      const [positionsResponse, ordersResponse, historyResponse] = await Promise.allSettled([
        tradingApi?.getPositions(),
        tradingApi?.getPendingOrders(),
        tradingApi?.getTradeHistory(50)
      ]);

      // Process positions with additional mock data for better table display
      if (positionsResponse?.status === 'fulfilled' && positionsResponse?.value) {
        setPositions(positionsResponse?.value);
      } else {
        // Enhanced mock positions with all required fields
        setPositions([
          {
            id: 1,
            symbol: 'EURUSD',
            type: 'buy',
            amount: 0.1,
            openPrice: 1.1675,
            currentPrice: 1.1680,
            closePrice: null,
            profit: 5.00,
            swap: -0.25,
            commission: 0,
            stopLoss: null,
            takeProfit: 1.1700,
            openTime: new Date(Date.now() - 2 * 60 * 60 * 1000)
          },
          {
            id: 2,
            symbol: 'GBPUSD',
            type: 'sell',
            amount: 0.2,
            openPrice: 1.3430,
            currentPrice: 1.3425,
            closePrice: null,
            profit: 10.00,
            swap: 0.15,
            commission: 0,
            stopLoss: 1.3450,
            takeProfit: 1.3400,
            openTime: new Date(Date.now() - 4 * 60 * 60 * 1000)
          },
          {
            id: 3,
            symbol: 'USDJPY',
            type: 'buy',
            amount: 0.15,
            openPrice: 149.25,
            currentPrice: 149.45,
            closePrice: null,
            profit: 15.75,
            swap: -0.45,
            commission: 0,
            stopLoss: 148.50,
            takeProfit: 150.00,
            openTime: new Date(Date.now() - 6 * 60 * 60 * 1000)
          },
          {
            id: 4,
            symbol: 'XAUUSD',
            type: 'sell',
            amount: 0.05,
            openPrice: 1935.50,
            currentPrice: 1930.25,
            closePrice: null,
            profit: 26.25,
            swap: 0,
            commission: 0,
            stopLoss: 1945.00,
            takeProfit: 1920.00,
            openTime: new Date(Date.now() - 8 * 60 * 60 * 1000)
          }
        ]);
      }

      // Process pending orders with enhanced data
      if (ordersResponse?.status === 'fulfilled' && ordersResponse?.value) {
        setPendingOrders(ordersResponse?.value);
      } else {
        // Enhanced mock orders with diverse trading pairs
        setPendingOrders([
          {
            id: 5,
            symbol: 'AUDUSD',
            type: 'buy_limit',
            amount: 0.25,
            openPrice: 0.6520,
            currentPrice: 0.6545,
            closePrice: null,
            profit: 0,
            swap: 0,
            commission: 0,
            stopLoss: 0.6500,
            takeProfit: 0.6580,
            openTime: new Date(Date.now() - 30 * 60 * 1000)
          },
          {
            id: 6,
            symbol: 'NZDUSD',
            type: 'sell_stop',
            amount: 0.1,
            openPrice: 0.5850,
            currentPrice: 0.5875,
            closePrice: null,
            profit: 0,
            swap: 0,
            commission: 0,
            stopLoss: 0.5900,
            takeProfit: 0.5800,
            openTime: new Date(Date.now() - 45 * 60 * 1000)
          },
          {
            id: 7,
            symbol: 'USDCAD',
            type: 'buy_stop',
            amount: 0.3,
            openPrice: 1.3620,
            currentPrice: 1.3595,
            closePrice: null,
            profit: 0,
            swap: 0,
            commission: 0,
            stopLoss: 1.3570,
            takeProfit: 1.3680,
            openTime: new Date(Date.now() - 60 * 60 * 1000)
          },
          {
            id: 8,
            symbol: 'EURGBP',
            type: 'sell_limit',
            amount: 0.15,
            openPrice: 0.8735,
            currentPrice: 0.8742,
            closePrice: null,
            profit: 0,
            swap: 0,
            commission: 0,
            stopLoss: 0.8765,
            takeProfit: 0.8700,
            openTime: new Date(Date.now() - 90 * 60 * 1000)
          },
          {
            id: 9,
            symbol: 'CHFJPY',
            type: 'buy_limit',
            amount: 0.08,
            openPrice: 165.80,
            currentPrice: 165.95,
            closePrice: null,
            profit: 0,
            swap: 0,
            commission: 0,
            stopLoss: 165.30,
            takeProfit: 166.50,
            openTime: new Date(Date.now() - 2 * 60 * 60 * 1000)
          }
        ]);
      }

      // Process trade history
      if (historyResponse?.status === 'fulfilled' && historyResponse?.value) {
        setTradeHistory(historyResponse?.value);
      } else {
        // Fallback mock history
        setTradeHistory([
          {
            id: 100,
            symbol: 'USDJPY',
            type: 'buy',
            volume: 0.15,
            openPrice: 149.25,
            closePrice: 149.45,
            profit: 15.75,
            swap: -0.45,
            commission: 0,
            openTime: new Date(Date.now() - 24 * 60 * 60 * 1000),
            closeTime: new Date(Date.now() - 22 * 60 * 60 * 1000)
          },
          {
            id: 101,
            symbol: 'AUDUSD',
            type: 'sell',
            volume: 0.25,
            openPrice: 0.6545,
            closePrice: 0.6530,
            profit: 37.50,
            swap: 0.25,
            commission: 0,
            openTime: new Date(Date.now() - 48 * 60 * 60 * 1000),
            closeTime: new Date(Date.now() - 45 * 60 * 60 * 1000)
          },
          {
            id: 102,
            symbol: 'EURUSD',
            type: 'buy',
            volume: 0.1,
            openPrice: 1.1650,
            closePrice: 1.1630,
            profit: -20.00,
            swap: 0,
            commission: 0,
            openTime: new Date(Date.now() - 72 * 60 * 60 * 1000),
            closeTime: new Date(Date.now() - 70 * 60 * 60 * 1000)
          },
          {
            id: 103,
            symbol: 'GBPUSD',
            type: 'sell',
            volume: 0.2,
            openPrice: 1.3450,
            closePrice: 1.3420,
            profit: 60.00,
            swap: -0.15,
            commission: 0,
            openTime: new Date(Date.now() - 96 * 60 * 60 * 1000),
            closeTime: new Date(Date.now() - 94 * 60 * 60 * 1000)
          },
          {
            id: 104,
            symbol: 'XAUUSD',
            type: 'buy',
            volume: 0.05,
            openPrice: 1925.50,
            closePrice: 1920.00,
            profit: -27.50,
            swap: 0,
            commission: 0,
            openTime: new Date(Date.now() - 120 * 60 * 60 * 1000),
            closeTime: new Date(Date.now() - 118 * 60 * 60 * 1000)
          }
        ]);
      }

    } catch (error) {
      console.error('Failed to load trading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const connectToWebSocket = () => {
    try {
      setConnectionStatus('connecting');
      
      // Connect to WebSocket for real-time position updates
      tradingApi?.connectWebSocket(
        (data) => handleWebSocketMessage(data),
        (error) => {
          console.error('WebSocket error:', error);
          setConnectionStatus('error');
        },
        (event) => {
          setConnectionStatus('disconnected');
        }
      );

      // Subscribe to position and order updates
      setTimeout(() => {
        if (tradingApi?.getConnectionStatus() === 'connected') {
          tradingApi?.subscribe('positions', null, handlePositionUpdate);
          tradingApi?.subscribe('orders', null, handleOrderUpdate);
          setConnectionStatus('connected');
        }
      }, 1000);

    } catch (error) {
      console.error('WebSocket connection failed:', error);
      setConnectionStatus('error');
    }
  };

  const handleWebSocketMessage = (data) => {
    switch (data?.type) {
      case 'position_update':
        handlePositionUpdate(data);
        break;
      case 'order_update':
        handleOrderUpdate(data);
        break;
      default:
        break;
    }
  };

  const closePosition = async (positionId) => {
    try {
      await tradingApi?.closePosition(positionId);
      console.log('Position closed:', positionId);
      
      // Remove from positions list
      setPositions(prev => prev?.filter(p => p?.id !== positionId));
      
    } catch (error) {
      console.error('Failed to close position:', error);
      // Show error message to user
    }
  };

  const editPosition = (positionId) => {
    // TODO: Implement position editing functionality
    console.log('Edit position:', positionId);
  };

  const cancelOrder = async (orderId) => {
    try {
      await tradingApi?.cancelOrder(orderId);
      console.log('Order cancelled:', orderId);
      
      // Remove from pending orders
      setPendingOrders(prev => prev?.filter(o => o?.id !== orderId));
      
    } catch (error) {
      console.error('Failed to cancel order:', error);
      // Show error message to user
    }
  };

  const editOrder = (orderId) => {
    // TODO: Implement order editing functionality
    console.log('Edit order:', orderId);
  };

  // Combined data for unified table
  const combinedData = useMemo(() => {
    const combined = [...positions, ...pendingOrders];
    
    // Apply search filter
    const filtered = searchTerm
      ? combined?.filter(item => 
          item?.symbol?.toLowerCase()?.includes(searchTerm?.toLowerCase())
        )
      : combined;

    return filtered;
  }, [positions, pendingOrders, searchTerm]);

  // Pagination logic
  const totalPages = Math.ceil(combinedData?.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedData = combinedData?.slice(startIndex, startIndex + itemsPerPage);

  // Reset to first page when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const formatPrice = (price) => {
    if (!price) return '--';
    if (price > 1000) return price?.toFixed(2);
    if (price > 10) return price?.toFixed(4);
    return price?.toFixed(5);
  };

  const formatTime = (date) => {
    if (!date) return '--';
    return date?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (date) => {
    if (!date) return '--';
    return date?.toLocaleDateString();
  };

  const getProfitColor = (profit) => {
    return profit >= 0 ? 'text-green-600' : 'text-red-600';
  };

  const getTypeColor = (type) => {
    return type?.includes('buy') ? 'text-green-600' : 'text-red-600';
  };

  const renderPositionsTable = () => (
    <div className="space-y-4">
      {/* Search and Controls */}
      <div className="flex items-center justify-between">
        <div className="flex-1 max-w-sm">
          <Input
            type="text"
            placeholder={t('searchSymbol')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e?.target?.value)}
            className="h-9"
          />
        </div>
        <div className="text-sm text-muted-foreground">
          {combinedData?.length} {t('items')}
        </div>
      </div>

      {/* Desktop Table View */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left py-2 px-2 font-medium text-muted-foreground">{t('id')}</th>
              <th className="text-left py-2 px-2 font-medium text-muted-foreground">{t('symbol')}</th>
              <th className="text-left py-2 px-2 font-medium text-muted-foreground">{t('type')}</th>
              <th className="text-left py-2 px-2 font-medium text-muted-foreground">{t('amount')}</th>
              <th className="text-left py-2 px-2 font-medium text-muted-foreground">{t('openTime')}</th>
              <th className="text-left py-2 px-2 font-medium text-muted-foreground">{t('openPrice')}</th>
              <th className="text-left py-2 px-2 font-medium text-muted-foreground">{t('closePrice')}</th>
              <th className="text-left py-2 px-2 font-medium text-muted-foreground">{t('stopLoss')}</th>
              <th className="text-left py-2 px-2 font-medium text-muted-foreground">{t('takeProfit')}</th>
              <th className="text-left py-2 px-2 font-medium text-muted-foreground">{t('profit')}</th>
              <th className="text-left py-2 px-2 font-medium text-muted-foreground">{t('swap')}</th>
              <th className="text-left py-2 px-2 font-medium text-muted-foreground">{t('commission')}</th>
              <th className="text-left py-2 px-2 font-medium text-muted-foreground">{t('actions')}</th>
            </tr>
          </thead>
          <tbody>
            {paginatedData?.map((item) => (
              <tr key={item?.id} className="border-b border-border/50 hover:bg-muted/20">
                <td className="py-2 px-2 text-foreground">{item?.id}</td>
                <td className="py-2 px-2 font-medium text-foreground">{item?.symbol}</td>
                <td className="py-2 px-2">
                  <span className={`text-xs font-medium ${getTypeColor(item?.type)}`}>
                    {item?.type?.toUpperCase()}
                  </span>
                </td>
                <td className="py-2 px-2 text-foreground">{item?.amount || item?.volume}</td>
                <td className="py-2 px-2 text-muted-foreground">
                  <div className="text-xs">
                    <div>{formatDate(item?.openTime)}</div>
                    <div>{formatTime(item?.openTime)}</div>
                  </div>
                </td>
                <td className="py-2 px-2 text-foreground">{formatPrice(item?.openPrice)}</td>
                <td className="py-2 px-2 text-foreground">{formatPrice(item?.closePrice || item?.currentPrice)}</td>
                <td className="py-2 px-2 text-foreground">{formatPrice(item?.stopLoss || item?.sl)}</td>
                <td className="py-2 px-2 text-foreground">{formatPrice(item?.takeProfit || item?.tp)}</td>
                <td className="py-2 px-2">
                  <span className={`font-medium ${getProfitColor(item?.profit || 0)}`}>
                    {item?.profit >= 0 ? '+' : ''}${(item?.profit || 0)?.toFixed(2)}
                  </span>
                </td>
                <td className="py-2 px-2 text-foreground">${(item?.swap || 0)?.toFixed(2)}</td>
                <td className="py-2 px-2 text-foreground">${(item?.commission || 0)?.toFixed(2)}</td>
                <td className="py-2 px-2">
                  <div className="flex items-center space-x-1">
                    <button
                      onClick={() => item?.type?.includes('_') ? editOrder(item?.id) : editPosition(item?.id)}
                      className="p-1 text-blue-600 hover:text-blue-700 hover:bg-blue-100 rounded text-xs"
                      title={t('edit')}
                    >
                      <Icon name="Edit" size={14} />
                    </button>
                    <button
                      onClick={() => item?.type?.includes('_') ? cancelOrder(item?.id) : closePosition(item?.id)}
                      className="p-1 text-red-600 hover:text-red-700 hover:bg-red-100 rounded text-xs"
                      title={t('close')}
                    >
                      <Icon name="X" size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-3">
        {paginatedData?.map((item) => (
          <div key={item?.id} className="bg-muted/30 rounded-lg p-4 border border-border/50">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-2">
                <span className={`px-2 py-1 text-xs font-medium rounded ${getTypeColor(item?.type)} bg-current/10`}>
                  {item?.type?.toUpperCase()}
                </span>
                <span className="font-semibold text-foreground">{item?.symbol}</span>
                <span className="text-xs text-muted-foreground">#{item?.id}</span>
              </div>
              <div className="flex items-center space-x-1">
                <button
                  onClick={() => item?.type?.includes('_') ? editOrder(item?.id) : editPosition(item?.id)}
                  className="p-1 text-blue-600 hover:text-blue-700 hover:bg-blue-100 rounded"
                  title={t('edit')}
                >
                  <Icon name="Edit" size={16} />
                </button>
                <button
                  onClick={() => item?.type?.includes('_') ? cancelOrder(item?.id) : closePosition(item?.id)}
                  className="p-1 text-red-600 hover:text-red-700 hover:bg-red-100 rounded"
                  title={t('close')}
                >
                  <Icon name="X" size={16} />
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-xs text-muted-foreground block">{t('amount')}</span>
                <span className="font-medium text-foreground">{item?.amount || item?.volume}</span>
              </div>
              <div>
                <span className="text-xs text-muted-foreground block">{t('profit')}</span>
                <span className={`font-medium ${getProfitColor(item?.profit || 0)}`}>
                  {item?.profit >= 0 ? '+' : ''}${(item?.profit || 0)?.toFixed(2)}
                </span>
              </div>
              <div>
                <span className="text-xs text-muted-foreground block">{t('openPrice')}</span>
                <span className="font-medium text-foreground">{formatPrice(item?.openPrice)}</span>
              </div>
              <div>
                <span className="text-xs text-muted-foreground block">{t('currentPrice')}</span>
                <span className="font-medium text-foreground">{formatPrice(item?.closePrice || item?.currentPrice)}</span>
              </div>
              <div>
                <span className="text-xs text-muted-foreground block">{t('stopLoss')}</span>
                <span className="text-foreground">{formatPrice(item?.stopLoss || item?.sl) || '--'}</span>
              </div>
              <div>
                <span className="text-xs text-muted-foreground block">{t('takeProfit')}</span>
                <span className="text-foreground">{formatPrice(item?.takeProfit || item?.tp) || '--'}</span>
              </div>
            </div>

            <div className="flex justify-between items-center mt-3 pt-3 border-t border-border/30 text-xs text-muted-foreground">
              <span>{formatDate(item?.openTime)} {formatTime(item?.openTime)}</span>
              <div className="flex space-x-4">
                <span>Swap: ${(item?.swap || 0)?.toFixed(2)}</span>
                <span>Kom: ${(item?.commission || 0)?.toFixed(2)}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {paginatedData?.length === 0 && (
        <div className="text-center py-8 text-muted-foreground">
          <Icon name="Activity" size={32} className="mx-auto mb-2" />
          <p>{searchTerm ? t('noData') : t('noOpenPositions')}</p>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm">
          <div className="text-muted-foreground">
            {t('page')} {currentPage} {t('of')} {totalPages}
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 border border-border rounded text-foreground hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('previous')}
            </button>
            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-1 border border-border rounded text-foreground hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('next')}
            </button>
          </div>
        </div>
      )}
    </div>
  );

  const renderPendingOrders = () => (
    <div className="space-y-2">
      {pendingOrders?.map((order) => (
        <div key={order?.id} className="p-3 bg-muted/30 rounded-md border border-border/50">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-2">
              <span className={`px-2 py-1 text-xs font-medium rounded ${getTypeColor(order?.type)} bg-current/10`}>
                {order?.type?.toUpperCase()}
              </span>
              <span className="font-semibold text-foreground">{order?.symbol}</span>
              <span className="text-sm text-muted-foreground">{order?.amount || order?.volume}</span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-sm font-medium text-foreground">
                {formatPrice(order?.orderPrice || order?.openPrice)}
              </span>
              <button
                onClick={() => cancelOrder(order?.id)}
                className="text-red-600 hover:text-red-700 text-xs p-1 hover:bg-red-100 rounded"
                title={t('cancel')}
              >
                <Icon name="X" size={12} />
              </button>
            </div>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs text-muted-foreground">
            <div>
              <span className="block">{t('orderPrice')}: {formatPrice(order?.orderPrice || order?.openPrice)}</span>
              <span className="block">{t('currentPrice')}: {formatPrice(order?.currentPrice)}</span>
            </div>
            <div>
              <span className="block">SL: {formatPrice(order?.stopLoss || order?.sl) || '--'}</span>
              <span className="block">TP: {formatPrice(order?.takeProfit || order?.tp) || '--'}</span>
            </div>
            <div>
              <span className="block">{t('placed')}: {formatTime(order?.placeTime || order?.openTime)}</span>
              <span className="block">{t('expires')}: {order?.expiry ? formatTime(order?.expiry) : t('gtc')}</span>
            </div>
            <div>
              <span className="block">{formatDate(order?.placeTime || order?.openTime)}</span>
            </div>
          </div>
        </div>
      ))}
      
      {pendingOrders?.length === 0 && (
        <div className="text-center py-8 text-muted-foreground">
          <Icon name="Clock" size={32} className="mx-auto mb-2" />
          <p>{t('noOrdersAvailable')}</p>
        </div>
      )}
    </div>
  );

  const renderTradeHistory = () => (
    <div className="space-y-2">
      {tradeHistory?.map((trade) => (
        <div key={trade?.id} className="p-3 bg-muted/30 rounded-md">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-2">
              <span className={`px-2 py-1 text-xs font-medium rounded ${getTypeColor(trade?.type)} bg-current/10`}>
                {trade?.type?.toUpperCase()}
              </span>
              <span className="font-semibold text-foreground">{trade?.symbol}</span>
              <span className="text-sm text-muted-foreground">{trade?.volume}</span>
            </div>
            <span className={`text-sm font-medium ${getProfitColor(trade?.profit)}`}>
              {trade?.profit >= 0 ? '+' : ''}${trade?.profit?.toFixed(2)}
            </span>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs text-muted-foreground">
            <div>
              <span className="block">Open: {formatPrice(trade?.openPrice)}</span>
              <span className="block">Close: {formatPrice(trade?.closePrice)}</span>
            </div>
            <div>
              <span className="block">Swap: ${trade?.swap?.toFixed(2)}</span>
              <span className="block">Commission: ${trade?.commission?.toFixed(2)}</span>
            </div>
            <div>
              <span className="block">Opened: {formatTime(trade?.openTime)}</span>
              <span className="block">Closed: {formatTime(trade?.closeTime)}</span>
            </div>
            <div>
              <span className="block">{formatDate(trade?.openTime)}</span>
              <span className="block">{formatDate(trade?.closeTime)}</span>
            </div>
          </div>
        </div>
      ))}
      
      {tradeHistory?.length === 0 && (
        <div className="text-center py-8 text-muted-foreground">
          <Icon name="History" size={32} className="mx-auto mb-2" />
          <p>{t('noTradeHistory')}</p>
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className={`h-full bg-card flex items-center justify-center ${isRTL ? 'rtl' : ''}`}>
        <div className="text-center">
          <Icon name="Loader" size={32} className="text-muted-foreground mb-4 animate-spin" />
          <p className="text-muted-foreground">{t('loadingPositions')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`h-full bg-card flex flex-col ${isRTL ? 'rtl' : ''}`}>
      {/* Header with Connection Status */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-foreground">{t('openPositionsAndPending')}</h2>
          <div className="flex items-center space-x-1">
            <div className={`w-2 h-2 rounded-full ${
              connectionStatus === 'connected' ? 'bg-green-500' : 
              connectionStatus === 'connecting' ? 'bg-yellow-500' : 'bg-red-500'
            }`}></div>
            <span className="text-xs text-muted-foreground">
              {connectionStatus === 'connected' ? 'Live' : connectionStatus}
            </span>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex space-x-1 bg-muted p-1 rounded">
          <button
            onClick={() => setActiveTab('positions')}
            className={`flex-1 py-2 px-3 text-sm font-medium rounded transition-colors ${
              activeTab === 'positions' ?'bg-background text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            {t('openPositionsAndPending')} ({combinedData?.length})
          </button>
          <button
            onClick={() => setActiveTab('orders')}
            className={`flex-1 py-2 px-3 text-sm font-medium rounded transition-colors ${
              activeTab === 'orders' ?'bg-background text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            {t('orders')} ({pendingOrders?.length})
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`flex-1 py-2 px-3 text-sm font-medium rounded transition-colors ${
              activeTab === 'history' ?'bg-background text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            {t('history')}
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {activeTab === 'positions' && renderPositionsTable()}
        {activeTab === 'orders' && renderPendingOrders()}
        {activeTab === 'history' && renderTradeHistory()}
      </div>
    </div>
  );
};

export default PositionsPanel;